import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../lib/api-client';
import { useAuthStore, type AuthUser } from '../stores/useAuthStore';

export function useAuth(): {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refetch: () => void;
} {
  const { user, isLoading, setUser, setLoading } = useAuthStore();

  const accessToken = useAuthStore((state) => state.accessToken);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['auth', 'me', accessToken],
    queryFn: async () => {
      const response = await apiClient.get<AuthUser>('/api/auth/me');
      return response.data;
    },
    enabled: !!accessToken,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data) {
      setUser(data);
    } else if (!isFetching) {
      setUser(null);
    }
  }, [data, isFetching, setUser]);

  useEffect(() => {
    setLoading(isFetching);
  }, [isFetching, setLoading]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    refetch,
  };
}

export async function logout(): Promise<void> {
  await apiClient.post('/api/auth/logout');
  useAuthStore.getState().logout();
  window.location.href = '/login';
}
